import {ActionMethod} from 'constants/AnalyticsConstants';
import {EventConstants} from 'constants/EventConstants';
import AppDispatcher from 'lib/Dispatcher';
import {StructuredTableMappingActionOptions} from 'notebook/structured-tables/structured-table-mappings/StructuredTableMappingConstants';
import {
  StructuredTableMappingsGlobalState,
  StructuredTableMappingsStateManager,
} from 'notebook/structured-tables/structured-table-mappings/StructuredTableMappingsState';
import {UnknownStructuredTable} from 'notebook/structured-tables/StructuredTableConstants';
import {TableColumnType} from 'notebook/tables/TableConstants';
import {ProcessTaskStructuredTable} from 'processes/tables/ProcessTaskStructuredTableAdapter';
import removeNulls from 'util/removeNulls';

import {RowSubmissionRange} from '../action-functions/makeSubmitRowActionFunction';

export interface InvokeStructuredTableMappingActionPayload {
  actionType: typeof EventConstants.STRUCTURED_TABLE_INVOKE_MAPPINGS;
  fileId: string;
  sourceTableProvenanceUuid: string;
  options: StructuredTableMappingActionOptions;
}

/** For a given note and source table, initiate the table mapping process. This should be called in table
 * action's doPostRefresh. The table provided should still have its unfinalized rows.
 */
export default function invokeStructuredTableMappings(
  fileId: string,
  sourceStructuredTable: UnknownStructuredTable,
  selection?: RowSubmissionRange
): void {
  // if we don't have any existing mappings for the table, save ourselves the trouble and return early
  if (
    !StructuredTableMappingsStateManager.hasMappingsFromSourceTable(
      StructuredTableMappingsGlobalState.getState(fileId),
      sourceStructuredTable.provenanceUuid
    )
  ) {
    return;
  }

  const payload: InvokeStructuredTableMappingActionPayload = {
    actionType: EventConstants.STRUCTURED_TABLE_INVOKE_MAPPINGS,
    fileId,
    sourceTableProvenanceUuid: sourceStructuredTable.provenanceUuid,
    options: {
      sourceTableRowIndexes: getAddedRowIndexes(sourceStructuredTable, selection),
      analytics: {
        actionMethod: ActionMethod.STRUCTURED_TABLE_SUBMISSION,
      },
    },
  };
  AppDispatcher.dispatch(payload);
}

/** Given a process task input table and a set of task ids, find the row indexes for those tasks.
 * Returns indexes in the order of the table rows. */
export function getProcessTaskInputRowIndexes(
  processTaskStructuredTable: ProcessTaskStructuredTable,
  taskIds: Array<string>
): Array<number> {
  const processTaskColumnIndex = processTaskStructuredTable.columns.findIndex(
    (column) => column.columnType === TableColumnType.PROCESS_TASK
  );
  return removeNulls(
    processTaskStructuredTable.rows.map((row, index) => {
      const taskCellTypedValue = row.cells[processTaskColumnIndex].typedValue;
      if (taskCellTypedValue?.type === 'WORKFLOW_TASK' && taskIds.includes(taskCellTypedValue.id)) {
        return index;
      }
      return null;
    })
  );
}

/** Given a table that's being submitted, returns the rows that are newly added. (Notably, this does
 * not include rows that are just modified) */
function getAddedRowIndexes(
  structuredTable: UnknownStructuredTable,
  selection?: RowSubmissionRange
): Array<number> {
  return removeNulls(
    structuredTable.rows.map((row, index) => {
      if (selection && (index < selection.start || index >= selection.end)) {
        return null;
      }
      return row.isFinalized ? null : index;
    })
  );
}
